import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildApiUrl } from "../helpers/base_url";
import { requestFcmToken } from "../services/fcm";

function LoginRegister() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("login");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Login form state
  const [loginForm, setLoginForm] = useState({
    email: "",
    password: "",
  });

  // Register form state
  const [registerForm, setRegisterForm] = useState({
    userEmail: "",
    password: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    address: "",
    city: "",
    state: "",
    country: "Nigeria",
    zipCode: "",
    companyName: "",
    profileImageUrl: "",
  });

  const handleLoginChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLoginForm({
      ...loginForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleRegisterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRegisterForm({
      ...registerForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setMessage({
        type: "error",
        text: "Please select an image file.",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setMessage({
        type: "error",
        text: "Image size must be less than 5MB.",
      });
      return;
    }

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file);
    setImagePreview(localPreview);

    setUploadingImage(true);
    setMessage({ type: "", text: "" });

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(buildApiUrl("/file/"), {
        method: "POST",
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success && data.data) {
        // Revoke local preview URL
        URL.revokeObjectURL(localPreview);
        // Update form with the image URL
        setRegisterForm({
          ...registerForm,
          profileImageUrl: data.data,
        });
        // Set preview to uploaded URL
        setImagePreview(data.data);
        setMessage({
          type: "success",
          text: "Image uploaded successfully!",
        });
      } else {
        // Revoke local preview on error
        URL.revokeObjectURL(localPreview);
        setImagePreview(null);
        setMessage({
          type: "error",
          text: data.message || "Failed to upload image. Please try again.",
        });
      }
    } catch (error) {
      // Revoke local preview on error
      if (imagePreview && imagePreview.startsWith("blob:")) {
        URL.revokeObjectURL(imagePreview);
      }
      setImagePreview(null);
      setMessage({
        type: "error",
        text: "Network error. Please check your connection.",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetch(buildApiUrl("/api/auth/login"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginForm),
      });

      const data = await response.json();

      // Handle API response format: { status, message, data: { accessToken, ... } } or direct { accessToken, ... }
      const responseData = data?.data || data;
      const accessToken =
        responseData?.accessToken ||
        responseData?.token ||
        responseData?.jwt ||
        data?.accessToken ||
        data?.token;

      const user = responseData?.user || data?.user;
      const userType = user?.userType || responseData?.userType || data?.userType;

      if (userType && userType !== "CLIENT") {
        setMessage({
          type: "error",
          text: "Please login via the vendor portal.",
        });
        setTimeout(() => {
          window.location.href = "https://vendor.eventnova.com.ng/pages/login";
        }, 1200);
        return;
      }

      if (response.ok && accessToken) {
        // Save login data to localStorage
        localStorage.setItem("accessToken", accessToken);

        const refreshToken = responseData?.refreshToken || data?.refreshToken;
        if (refreshToken) {
          localStorage.setItem("refreshToken", refreshToken);
        }

        const tokenType = responseData?.tokenType || data?.tokenType;
        if (tokenType) {
          localStorage.setItem("tokenType", tokenType);
        }

        if (user) {
          localStorage.setItem("user", JSON.stringify(user));
        }

        const expiresIn = responseData?.expiresIn || data?.expiresIn;
        if (expiresIn) {
          localStorage.setItem("expiresIn", expiresIn.toString());
        }

        localStorage.setItem("loginTime", Date.now().toString());

        if (user?.id) {
          requestFcmToken(String(user.id)).catch((error) =>
            console.warn("Failed to request FCM token after login:", error)
          );
        }

        // Dispatch custom event to notify other parts of the app
        window.dispatchEvent(new Event("localStorageChange"));

        setMessage({
          type: "success",
          text: "Login successful! Redirecting...",
        });

        // Clear login form
        setLoginForm({
          email: "",
          password: "",
        });

        // Redirect to homepage using React Router
        setTimeout(() => {
          navigate("/", { replace: true });
        }, 1000);
      } else {
        setMessage({
          type: "error",
          text: data?.message || "Login failed. Please check your credentials.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Network error. Please check your connection.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const response = await fetch(buildApiUrl("/api/clients"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(registerForm),
      });

      const data = await response.json();

      if (data.success) {
        setMessage({
          type: "success",
          text: "Registration successful! You can now login.",
        });
        setActiveTab("login");
        setRegisterForm({
          userEmail: "",
          password: "",
          firstName: "",
          lastName: "",
          phoneNumber: "",
          address: "",
          city: "",
          state: "",
          country: "Nigeria",
          zipCode: "",
          companyName: "",
          profileImageUrl: "",
        });
        setImagePreview(null);
        // Reset file input
        const fileInput = document.querySelector(
          'input[type="file"]'
        ) as HTMLInputElement;
        if (fileInput) fileInput.value = "";
      } else {
        setMessage({
          type: "error",
          text: data.message || "Registration failed. Please try again.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Network error. Please check your connection.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="breadcrumb-area pt-35 pb-35 bg-gray-3">
        <div className="container">
          <div className="breadcrumb-content text-center">
            <ul>
              <li>
                <a href="/">Home</a>
              </li>
              <li className="active">Login/Register</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="login-register-area pt-100 pb-100">
        <div className="container">
          {/* Client Login Header */}
          {/* <div style={{ textAlign: "center", marginBottom: 40 }}>
            <h1
              style={{
                fontSize: "clamp(28px, 4vw, 36px)",
                fontWeight: 800,
                color: "#1f2937",
                marginBottom: 12,
              }}
            >
              Client Portal
            </h1>
            <p
              style={{
                fontSize: 16,
                color: "#6b7280",
                maxWidth: 600,
                margin: "0 auto",
              }}
            >
              Login or register as a client to book vendors and manage your events
            </p>
          </div> */}

          <div className="row">
            {/* Vendor Card - Now at the top/side */}
            <div className="col-lg-4 col-md-12 mb-4 mb-lg-0">
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(108,99,255,0.15), rgba(99,102,241,0.12))",
                  border: "2px solid rgba(108,99,255,0.4)",
                  borderRadius: 24,
                  padding: "32px 28px",
                  boxShadow: "0 20px 50px rgba(108,99,255,0.2)",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 48,
                    marginBottom: 16,
                    textAlign: "center",
                  }}
                >
                  🎯
                </div>
                <h3
                  style={{
                    fontWeight: 800,
                    marginBottom: 12,
                    color: "#1f2937",
                    fontSize: 24,
                    textAlign: "center",
                  }}
                >
                  Are you a vendor?
                </h3>
                <p
                  style={{
                    color: "#4b5563",
                    marginBottom: 24,
                    fontSize: 15,
                    lineHeight: 1.6,
                    textAlign: "center",
                  }}
                >
                  Offer premium services on EventNova and reach thousands of
                  clients. Access the vendor dashboard to manage your profile,
                  bookings, and payments effortlessly.
                </p>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                  }}
                >
                  <a
                    href="https://vendor.eventnova.com.ng/pages/login"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "linear-gradient(135deg, #6C63FF 0%, #9D8DF1 100%)",
                      color: "#fff",
                      borderRadius: 12,
                      padding: "14px 24px",
                      fontWeight: 700,
                      textDecoration: "none",
                      boxShadow: "0 10px 30px rgba(108,99,255,0.3)",
                      textAlign: "center",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 15px 40px rgba(108,99,255,0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 10px 30px rgba(108,99,255,0.3)";
                    }}
                  >
                    Vendor Login
                  </a>
                  <a
                    href="https://vendor.eventnova.com.ng/pages/register"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      background: "rgba(108,99,255,0.1)",
                      color: "#6C63FF",
                      borderRadius: 12,
                      padding: "14px 24px",
                      fontWeight: 700,
                      textDecoration: "none",
                      border: "2px solid rgba(108,99,255,0.5)",
                      textAlign: "center",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "rgba(108,99,255,0.15)";
                      e.currentTarget.style.borderColor = "#6C63FF";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(108,99,255,0.1)";
                      e.currentTarget.style.borderColor = "rgba(108,99,255,0.5)";
                    }}
                  >
                    Become a Vendor
                  </a>
                </div>
              </div>
            </div>

            {/* Login/Register Form */}
            <div className="col-lg-8 col-md-12">
              <div className="login-register-wrapper">
                <div className="login-register-tab-list nav">
                  <button
                    type="button"
                    className={activeTab === "login" ? "active" : ""}
                    onClick={() => setActiveTab("login")}
                    style={{ cursor: "pointer", background: "none", border: "none" }}
                  >
                    <h4>Login</h4>
                  </button>
                  <button
                    type="button"
                    className={activeTab === "register" ? "active" : ""}
                    onClick={() => setActiveTab("register")}
                    style={{ cursor: "pointer", background: "none", border: "none" }}
                  >
                    <h4>Register</h4>
                  </button>
                </div>
                <div
                  style={{
                    textAlign: "center",
                    marginTop: 12,
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      background: "rgba(108,99,255,0.1)",
                      color: "#6C63FF",
                      padding: "6px 16px",
                      borderRadius: 20,
                      fontSize: 13,
                      fontWeight: 600,
                      border: "1px solid rgba(108,99,255,0.3)",
                    }}
                  >
                    Client Account
                  </span>
                </div>

                {/* Message Display */}
                {message.text && (
                  <div
                    style={{
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                      backgroundColor:
                        message.type === "success" ? "#d4edda" : "#f8d7da",
                      color: message.type === "success" ? "#155724" : "#721c24",
                      border: `1px solid ${
                        message.type === "success" ? "#c3e6cb" : "#f5c6cb"
                      }`,
                    }}
                  >
                    {message.text}
                  </div>
                )}

                <div className="tab-content">
                  {/* Login Tab */}
                  <div
                    id="lg1"
                    className={`tab-pane ${
                      activeTab === "login" ? "active" : ""
                    }`}
                    style={{
                      display: activeTab === "login" ? "block" : "none",
                    }}
                  >
                    <div className="login-form-container">
                      <div className="login-register-form">
                        <form onSubmit={handleLogin}>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={loginForm.email}
                            onChange={handleLoginChange}
                            required
                          />
                          <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={loginForm.password}
                            onChange={handleLoginChange}
                            required
                          />
                          <div className="button-box">
                            <div
                              className="login-toggle-btn"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 8,
                                marginBottom: 8,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                }}
                              >
                                <input type="checkbox" id="remember-me" />
                                <label htmlFor="remember-me">Remember me</label>
                              </div>
                              <button
                                type="button"
                                onClick={() => navigate("/auth/forgot-password")}
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  color: "#6C63FF",
                                  fontWeight: 500,
                                  cursor: "pointer",
                                  textDecoration: "underline",
                                  padding: 0,
                                  fontSize: 13,
                                }}
                              >
                                Forgot password?
                              </button>
                            </div>
                            <button type="submit" disabled={isLoading}>
                              <span>
                                {isLoading ? "Logging in..." : "Login"}
                              </span>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>

                  {/* Register Tab */}
                  <div
                    id="lg2"
                    className={`tab-pane ${
                      activeTab === "register" ? "active" : ""
                    }`}
                    style={{
                      display: activeTab === "register" ? "block" : "none",
                    }}
                  >
                    <div className="login-form-container">
                      <div className="login-register-form">
                        <form onSubmit={handleRegister}>
                          <div style={{ display: "flex", gap: "16px" }}>
                            <input
                              type="text"
                              name="firstName"
                              placeholder="First Name"
                              value={registerForm.firstName}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                            <input
                              type="text"
                              name="lastName"
                              placeholder="Last Name"
                              value={registerForm.lastName}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                          </div>
                          <input
                            type="email"
                            name="userEmail"
                            placeholder="Email Address"
                            value={registerForm.userEmail}
                            onChange={handleRegisterChange}
                            required
                          />
                          <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={registerForm.password}
                            onChange={handleRegisterChange}
                            required
                          />
                          <input
                            type="tel"
                            name="phoneNumber"
                            placeholder="Phone Number"
                            value={registerForm.phoneNumber}
                            onChange={handleRegisterChange}
                            required
                          />
                          <input
                            type="text"
                            name="companyName"
                            placeholder="Company Name (Optional)"
                            value={registerForm.companyName}
                            onChange={handleRegisterChange}
                          />
                          <input
                            type="text"
                            name="address"
                            placeholder="Address"
                            value={registerForm.address}
                            onChange={handleRegisterChange}
                            required
                          />
                          <div style={{ display: "flex", gap: "16px" }}>
                            <input
                              type="text"
                              name="city"
                              placeholder="City"
                              value={registerForm.city}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                            <input
                              type="text"
                              name="state"
                              placeholder="State"
                              value={registerForm.state}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                          </div>
                          <div style={{ display: "flex", gap: "16px" }}>
                            <input
                              type="text"
                              name="country"
                              placeholder="Country"
                              value={registerForm.country}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                            <input
                              type="text"
                              name="zipCode"
                              placeholder="Zip Code"
                              value={registerForm.zipCode}
                              onChange={handleRegisterChange}
                              required
                              style={{ flex: 1 }}
                            />
                          </div>
                          {/* Profile Image Upload */}
                          <div style={{ marginBottom: "16px" }}>
                            <label
                              style={{
                                display: "block",
                                marginBottom: "8px",
                                fontWeight: 600,
                                color: "#222",
                              }}
                            >
                              Profile Image (Optional)
                            </label>
                          <input
                              type="file"
                              accept="image/*"
                              onChange={handleImageUpload}
                              disabled={uploadingImage}
                              style={{
                                width: "100%",
                                padding: "10px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "14px",
                              }}
                            />
                            {uploadingImage && (
                              <div
                                style={{
                                  marginTop: "8px",
                                  color: "#6C63FF",
                                  fontSize: "14px",
                                }}
                              >
                                Uploading image...
                              </div>
                            )}
                            {imagePreview && (
                              <div
                                style={{
                                  marginTop: "12px",
                                  textAlign: "center",
                                }}
                              >
                                <img
                                  src={imagePreview}
                                  alt="Profile preview"
                                  style={{
                                    maxWidth: "150px",
                                    maxHeight: "150px",
                                    borderRadius: "8px",
                                    border: "2px solid #6C63FF",
                                    objectFit: "cover",
                                  }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    // Revoke blob URL if it's a local preview
                                    if (
                                      imagePreview &&
                                      imagePreview.startsWith("blob:")
                                    ) {
                                      URL.revokeObjectURL(imagePreview);
                                    }
                                    setImagePreview(null);
                                    setRegisterForm({
                                      ...registerForm,
                                      profileImageUrl: "",
                                    });
                                    // Reset file input
                                    const fileInput = document.querySelector(
                                      'input[type="file"]'
                                    ) as HTMLInputElement;
                                    if (fileInput) fileInput.value = "";
                                  }}
                                  style={{
                                    display: "block",
                                    margin: "8px auto 0",
                                    padding: "6px 12px",
                                    background: "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                  }}
                                >
                                  Remove Image
                                </button>
                              </div>
                            )}
                          </div>
                          <div className="button-box">
                            <button type="submit" disabled={isLoading}>
                              <span>
                                {isLoading ? "Creating Account..." : "Register"}
                              </span>
                            </button>
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default LoginRegister;
